import type { GlobalSingleRelationFieldId } from '#layers/data-model';
import type { ProjectCollectionsMap } from '~/types/constants/collections';
import { CREATE_MODE_ITEM_ID } from '#layers/data-model';
import { fieldIdToDisplayTemplateAst } from '#layers/data-model/utils/display-template';
import { findCollectionPrimitiveFieldByApiName, findCollectionRelationFieldByApiName } from '#layers/data-model/utils/fields/fields';
import { useFileCollection } from '~/composables/use-file-collection';

const MONOSPACE_FILES_THUMBNAIL_ID = '9b3ea5f2-e43b-44d0-83f3-e2d97dfff065' as GlobalSingleRelationFieldId;

export const PROJECT_COLLECTIONS: ProjectCollectionsMap = {
	MonospaceFile: {
		routes: {
			listing: {
				name: 'assets.library',
			},
			detail: (key) => {
				if (key === CREATE_MODE_ITEM_ID) {
					return {
						name: 'assets.library',
					};
				}

				return {
					name: 'assets.detail',
					params: {
						id: key?.id,
					},
				};
			},
		},
		schema: (collection) => {
			const filenameFieldId = findCollectionPrimitiveFieldByApiName(collection, 'fileName')?.id;
			return {
				fileName: {
					meta: {
						inputOptions: {
							disabled: true,
						},
					},
				},
				fileSize: {
					meta: {
						inputId: '$builtin:DisplayFileSize',
						displayId: '$builtin:DisplayFileSize',
					},
				},
				mediaType: {
					meta: {
						displayId: '$builtin:DisplayFormattedValue',
						displayOptions: {
							fontFamily: 'mono',
						},
						inputOptions: {
							disabled: true,
						},
					},
				},
				description: {
					meta: {
						inputId: '$builtin:InputTextarea',
					},
				},
				tags: {
					meta: {
						inputId: '$builtin:InputTags',
					},
				},
				$meta: {
					displayName: '$t:common.assets',
					displayTemplate: {
						type: 'doc',
						content: [
							{
								type: 'ref',
								attrs: {
									fieldId: MONOSPACE_FILES_THUMBNAIL_ID,
								},
							},

							...(filenameFieldId
								? [
										{
											type: 'text',
											// Acts as separator between the thumbnail and field id
											// TODO: Solve this properly, e.g. a separator node
											text: '  ',
										},
										{
											type: 'ref',
											attrs: {
												fieldId: filenameFieldId,
											},
										},
									] as const
								: []),
						],
					},
				},
				$extend({ addSingleRelationField }) {
					addSingleRelationField('$thumbnail', 'MonospaceFile', {
						id: MONOSPACE_FILES_THUMBNAIL_ID,
						fields: {
							'MonospaceFile.id': 'MonospaceFile.id',
						},
						meta: {
							displayName: '$t:common.preview',
							inputId: '$builtin:InputFileM2O',
							inputOptions: {
								showRemove: false,
							},
						},
					});
				},
			};
		},
		listing: {
			defaultView: () => {
				const { titleField, thumbnailField, mediaTypeField } = useFileCollection();

				return {
					layoutId: '$builtin:LayoutCards',
					displayOptions: {
						titleTemplate: titleField.value ? fieldIdToDisplayTemplateAst(titleField.value.id) : null,
						descriptionTemplate: mediaTypeField.value ? fieldIdToDisplayTemplateAst(mediaTypeField.value.id) : null,
						thumbnailField: thumbnailField.value?.id,
					},
				};
			},
		},
	},
	MonospacePolicy: {
		routes: {
			listing: {
				name: 'project.settings.access.policies.listing',
			},
			detail: (key) => ({
				name: 'project.settings.access.policies.detail',
				params: {
					policy: key === CREATE_MODE_ITEM_ID ? CREATE_MODE_ITEM_ID : key?.id,
				},
			}),
		},
		listing: {
			defaultView: (collection) => ({
				layoutId: '$builtin:LayoutTable',
				displayOptions: {
					visibleColumnIds: [
						findCollectionPrimitiveFieldByApiName(collection, 'name')?.id,
					],
				},
			}),
		},
		schema: {
			$meta: {
				displayTemplate: '{{ name }}',
			},
			id: {
				meta: {
					isHidden: true,
				},
			},
			name: {
				meta: {
					sort: 'a0',
				},
			},
			content: {
				meta: {
					displayName: '',
					inputId: '$builtin:InputPolicyDocument',
					sort: 'a1',
				},
			},
			filterFields: {
				meta: {
					isHidden: true,
				},
			},
			roles: {
				meta: {
					isHidden: true,
				},
			},
			isProtected: {
				meta: {
					isHidden: true,
				},
			},
		},
	},
	MonospaceRole: {
		routes: {
			listing: {
				name: 'project.settings.access.roles.listing',
			},
			detail: (key) => ({
				name: 'project.settings.access.roles.detail',
				params: {
					id: key === CREATE_MODE_ITEM_ID ? CREATE_MODE_ITEM_ID : key?.id,
				},
			}),
		},
		schema: {
			$meta: {
				displayTemplate: '{{ isPublic }} {{ name }}',
			},
			id: {
				meta: {
					isHidden: true,
				},
			},
			parent: {
				meta: {
					// TODO make field labels translatable
					// displayName: '$t:collections.monospace_role.parent.display_name',
					inputOptions: {
						showCreate: false,
						showEdit: false,
					},
				},
			},
			policies: {
				meta: {
					inputId: '$builtin:InputPolicyAssignment',
				},
			},
			directChildren: {
				meta: {
					isHidden: true,
				},
			},
			descendants: {
				meta: {
					isHidden: true,
				},
			},
			ancestors: {
				meta: {
					isHidden: true,
				},
			},
			users: {
				meta: {
					isHidden: true,
				},
			},
			isPublic: {
				meta: {
					isHidden: true,
					displayId: '$builtin:DisplayLabels',
					displayName: '$t:pages.settings.access.pages.roles.purpose',
					displayOptions: {
						displayStyle: 'dot-with-label',
						convertToTitleCase: false,
						options: [
							{
								value: true,
								label: '$t:pages.settings.access.pages.roles.public_badge.public',
								icon: 'lucide:globe',
								color: 'var(--ui-success)',
							},
							{
								value: false,
								label: '$t:pages.settings.access.pages.roles.public_badge.non_public',
								icon: 'lucide:users',
								color: 'var(--ui-warning)',
							},
						],
					},
				},
			},
		},
		listing: {
			defaultView: (collection) => ({
				layoutId: '$builtin:LayoutTable',
				displayOptions: {
					visibleColumnIds: [
						findCollectionPrimitiveFieldByApiName(collection, 'name')?.id,
						findCollectionRelationFieldByApiName(collection, 'parent')?.id,
						findCollectionRelationFieldByApiName(collection, 'policies')?.id,
						findCollectionPrimitiveFieldByApiName(collection, 'isPublic')?.id,
					],
					sorting: [
						{ fieldId: findCollectionPrimitiveFieldByApiName(collection, 'isPublic')?.id, direction: 'desc' },
					],
				},
			}),
		},
	},
	MonospaceSystemUser: {
		routes: {
			listing: {
				name: 'project.settings.access.members.listing',
			},
			detail: (key) => ({
				name: 'project.settings.access.members.detail',
				params: {
					id: key === CREATE_MODE_ITEM_ID ? CREATE_MODE_ITEM_ID : key?.id,
				},
			}),
		},
		listing: {
			defaultView: (collection) => ({
				layoutId: '$builtin:LayoutTable',
				columnOrder: [
					findCollectionPrimitiveFieldByApiName(collection, 'fullName')?.id,
				],
			}),
		},
		schema: {
			$meta: {
				// TODO update to use profile picture
				displayTemplate: '{{ fullName }}',
			},
			id: {
				meta: {
					isHidden: true,
					typeTemplateId: 'identifier',
				},
			},
			email: {
				meta: {
					inputOptions: {
						disabled: true,
					},
				},
			},
			fullName: {
				meta: {
					// TODO rework display names such that they can be translated from placeholders
					// displayName: '$t:common.name',
				},
			},
			roles: {
				meta: {
					inputId: '$builtin:InputRoleAssignment',
				},
			},
			avatarId: {
				meta: {
					isHidden: true,
				},
			},
			storageValues: {
				meta: {
					isHidden: true,
				},
			},
			isService: {
				meta: {
					isHidden: true,
				},
			},
			serviceOwnerId: {
				meta: {
					isHidden: true,
				},
			},
			appearance: {
				meta: {
					isHidden: true,
				},
			},
		},
	},
	MonospaceUserRole: {
		schema: {
			$meta: {
				displayTemplate: '{{ role }}',
			},
		},
	},
	MonospaceRolePolicy: {
		schema: {
			$meta: {
				displayTemplate: '{{ policy }}',
			},
		},
	},
} as const;
